import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Crosshair } from "@/components/Crosshair";
import { Camera, ArrowRight, Instagram } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AUTH_PHOTOS } from "@/assets";
import { usePageContent, AccentText } from "@/lib/pageContent";

// Public Elfsight widget UUID (an embed id, not a credential). Accepts either a bare
// UUID or an accidental full "elfsight-app-<uuid>" paste.
const RAW = (process.env.REACT_APP_ELFSIGHT_SOCIAL_WIDGET_ID || process.env.REACT_APP_ELFSIGHT || "").trim();
const WIDGET_ID = RAW.replace(/^elfsight-app-/, "");
const SCRIPT_SRC = "https://elfsightcdn.com/platform.js";

// Placeholder gallery shown ONLY when the widget is not configured (empty env var) or the
// Elfsight script is blocked / fails to populate. Mirrors the SITREP not_configured state.
const SHOTS = AUTH_PHOTOS;

function PlaceholderGallery() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="in-the-wild-gallery">
      {SHOTS.map((src, i) => (
        <div key={i} className="relative overflow-hidden border border-sniper bg-black" style={{ aspectRatio: "1 / 1" }}>
          <img src={src} alt="" className="w-full h-full object-cover opacity-80" />
        </div>
      ))}
    </div>
  );
}

// Lazy-loads the Elfsight platform script ONLY while this page is mounted (never app-wide),
// then hands the container off to Elfsight to render the configured Instagram / #OpticSwap feed.
function FieldFeed() {
  const ref = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ok | failed

  useEffect(() => {
    if (!WIDGET_ID) return;
    let script = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onerror = () => setStatus("failed");
      document.body.appendChild(script);
    }
    // If Elfsight hasn't populated the container after a grace period (blocked script, bad
    // id, offline), fall back to the placeholder gallery. The div stays mounted meanwhile.
    const t = setTimeout(() => {
      const el = ref.current;
      setStatus(el && el.childElementCount > 0 ? "ok" : "failed");
    }, 7000);
    return () => clearTimeout(t);
  }, []);

  // not_configured (no widget id) or the widget failed to render -> graceful placeholder.
  if (!WIDGET_ID || status === "failed") {
    return (
      <div data-testid="in-the-wild-feed-fallback">
        <PlaceholderGallery />
        <p className="mt-4 text-center text-[11px] font-mono uppercase tracking-[1.6px] text-muted-foreground" data-testid="in-the-wild-feed-note">
          Live field feed is being dialed in — follow{" "}
          <a href="https://instagram.com/opticswap" target="_blank" rel="noopener noreferrer" className="text-olive-bright hover:underline">@opticswap</a>{" "}
          and tag <span className="text-foreground">#OpticSwap</span> to land your gear here.
        </p>
      </div>
    );
  }

  // Panel matches the page: dark theme, square corners, panel over page background.
  return (
    <div className="border border-sniper bg-surface p-3 sm:p-4 w-full max-w-full overflow-hidden" data-testid="elfsight-wrap">
      <div
        ref={ref}
        className={`elfsight-app-${WIDGET_ID}`}
        data-elfsight-app-lazy
        data-testid="elfsight-widget"
      />
    </div>
  );
}

export default function InTheWild() {
  const { user } = useAuth();
  const { get } = usePageContent("in-the-wild");
  return (
    <div className="min-h-screen text-foreground" data-testid="in-the-wild-page">
      <Navbar />
      <div className="max-w-[1100px] mx-auto px-4 py-16 sm:py-20">
        <div className="text-center animate-fade-up">
          <span className="font-mono text-[10px] uppercase tracking-[3.4px] text-volt">{get("eyebrow", "Field Reports")}</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.08] mt-4">
            <AccentText text={get("heading", "IN THE *WILD.*")} />
          </h1>
          <p className="mt-5 text-base text-secondary-foreground font-body max-w-[58ch] mx-auto">
            {get("subhead", "Member gear, out where it earns its keep. Real setups from real hunts, ranges, and backcountry — shared by the OpticSwap community.")}
          </p>
          <p className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[1.6px] text-secondary-foreground">
            <Instagram className="w-3.5 h-3.5 text-olive-bright" strokeWidth={1.8} /> Pulled live from @opticswap · #OpticSwap
          </p>
        </div>

        <Crosshair className="my-14" />

        {/* live Instagram / #OpticSwap feed (Elfsight) with graceful placeholder */}
        <FieldFeed />

        <div className="mt-10 border border-olive/40 bg-olive/5 p-8 text-center" data-testid="in-the-wild-submit">
          <Camera className="w-7 h-7 text-olive-bright mx-auto" strokeWidth={1.6} />
          <p className="mt-3 font-heading text-2xl font-bold uppercase tracking-tight">{get("submit_heading", "Show us your setup")}</p>
          {user ? (
            <>
              <p className="mt-2 text-sm text-secondary-foreground font-body max-w-[52ch] mx-auto">
                {get("submit_body_member", "Post your gear on Instagram and tag #OpticSwap to feature it here. Add your handle to your profile so we can credit you.")}
              </p>
              <Link to="/settings/profile#socials" data-testid="in-the-wild-add-handles-cta" className="mt-6 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-5 py-3 transition-colors">
                Add your handles <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          ) : (
            <>
              <p className="mt-2 text-sm text-secondary-foreground font-body max-w-[52ch] mx-auto">
                {get("submit_body_guest", "Post your gear on Instagram and tag #OpticSwap to feature it here. Join the community and get on the wall.")}
              </p>
              <Link to="/join" data-testid="in-the-wild-join-cta" className="mt-6 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-5 py-3 transition-colors">
                Join the community <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
